import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatUsd } from '../lib/format';
import { CollectionsPanel, PHOTO_DRAG, type Collection } from '../components/CollectionsPanel';

interface Photo {
  uuid: string;
  src: string;
  thumb_url: string;
  original_key: string | null;
  title: string;
  photographer: string;
  price_base_cents: number;
  slug: string;
  is_active: number;
  purged_at: string | null;
}

export function PhotosList() {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [backfill, setBackfill] = useState<string | null>(null);
  const [collections, setCollections] = useState<Collection[]>([]);

  const load = () =>
    api
      .get<{ photos: Photo[] }>('/api/admin/photos')
      .then((d) => setPhotos(d.photos))
      .catch(console.error);

  useEffect(() => {
    load();
    api
      .get<{ collections: Collection[] }>('/api/admin/collections')
      .then((d) => setCollections(d.collections))
      .catch(console.error);
  }, []);

  const photosByUuid = useMemo(() => new Map(photos.map((p) => [p.uuid, p])), [photos]);
  const membership = useMemo(() => {
    const counts = new Map<string, number>();
    for (const c of collections) for (const u of c.photo_uuids) counts.set(u, (counts.get(u) ?? 0) + 1);
    return counts;
  }, [collections]);

  const legacyCount = photos.filter((p) => !p.original_key && !p.purged_at).length;

  // The API upgrades a few photos per call; loop until none remain.
  const onBackfill = async () => {
    const failed: string[] = [];
    try {
      for (;;) {
        setBackfill('Making originals private…');
        const r = await api.post<{ processed: number; failed: { uuid: string }[]; remaining: number }>(
          '/api/admin/photos/renditions/backfill'
        );
        failed.push(...r.failed.map((f) => f.uuid));
        if (r.remaining === 0 || r.processed === 0) break;
      }
      // Large legacy masters need a browser-made proxy, which the photo editor builds.
      setBackfill(failed.length ? `${failed.length} photo(s) need attention — open them and click "Make original private"` : null);
    } catch (err) {
      setBackfill(err instanceof Error ? err.message : 'Backfill failed');
    }
    load();
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400 }}>Photos</h1>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {backfill && <span style={{ fontSize: 13, color: '#6b7280' }}>{backfill}</span>}
          {legacyCount > 0 && (
            <button
              type="button"
              onClick={onBackfill}
              style={{
                padding: '10px 16px',
                background: '#fff',
                border: '1px solid #fcd34d',
                color: '#92400e',
                borderRadius: 6,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              Make {legacyCount} original{legacyCount === 1 ? '' : 's'} private
            </button>
          )}
          <Link
            to="/photos/new"
            style={{
              padding: '10px 16px',
              background: '#1a1a1a',
              color: '#fff',
              borderRadius: 6,
              fontSize: 13,
            }}
          >
            Add photo
          </Link>
        </div>
      </div>

      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start', marginTop: 24 }}>
        <div
          style={{
            flex: 2,
            minWidth: 0,
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
            gap: 16,
          }}
        >
          {photos.map((p) => (
            <Link
              key={p.uuid}
              to={`/photos/${p.uuid}`}
              draggable
              onDragStart={(e) => {
                e.dataTransfer.setData(PHOTO_DRAG, p.uuid);
                e.dataTransfer.effectAllowed = 'copy';
              }}
              style={{
                background: '#fff',
                border: '1px solid #e5e5e5',
                borderRadius: 8,
                overflow: 'hidden',
                textDecoration: 'none',
                color: 'inherit',
                opacity: p.is_active ? 1 : 0.5,
              }}
            >
              <img
                src={p.thumb_url}
                alt={p.title}
                draggable={false}
                style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
              />
              <div style={{ padding: 12 }}>
                <div style={{ fontWeight: 500 }}>{p.title}</div>
                <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
                  {p.photographer} · {formatUsd(p.price_base_cents)} base
                </div>
                <div style={{ fontSize: 11, color: membership.get(p.uuid) ? '#6b7280' : '#b45309', marginTop: 4 }}>
                  {membership.get(p.uuid)
                    ? `In ${membership.get(p.uuid)} collection${membership.get(p.uuid) === 1 ? '' : 's'}`
                    : 'Not in any collection'}
                </div>
                {p.purged_at ? (
                  <div style={{ fontSize: 11, color: '#991b1b', marginTop: 4 }}>Purged · thumbnail kept for orders</div>
                ) : !p.original_key && (
                  <div style={{ fontSize: 11, color: '#b45309', marginTop: 4 }}>Original is public</div>
                )}
              </div>
            </Link>
          ))}
        </div>
        <div style={{ flex: 1, minWidth: 280, position: 'sticky', top: 24 }}>
          <CollectionsPanel collections={collections} onChange={setCollections} photos={photosByUuid} />
        </div>
      </div>
    </div>
  );
}
